// src/components/Reservations.jsx  (FINAL - BRANCH TABLES COMPATIBLE - COPY/PASTE READY)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiJSON } from "../apiBase"; // ✅ IMPORTANT: uses cookie-auth + CSRF + x-branch headers
import "./reservations.css";

/* ------------ Config + helpers ------------ */

// ✅ Late rules (tweak to your operation)
const LATE_AFTER_MINUTES = 10; // becomes "late" after scheduled/start time + 10 min
const VERY_LATE_AFTER_MINUTES = 25; // becomes "very late" after + 25 min
const AUTO_REFRESH_SECONDS = 10; // auto refresh list while page is open

function pad(n) {
  return String(n).padStart(2, "0");
}
function nowLocalParts() {
  const d = new Date();
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
function digits(s) {
  return String(s || "").replace(/\D+/g, "");
}
function prettyError(e) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e instanceof Error) return e.message || String(e);
  try {
    if (Array.isArray(e))
      return e
        .map((x) => (typeof x === "string" ? x : JSON.stringify(x)))
        .join(" • ");
    if (typeof e === "object") {
      const msg = e.detail ?? e.error ?? e.message;
      if (typeof msg === "string") return msg;
      if (Array.isArray(msg))
        return msg
          .map((x) => (typeof x === "string" ? x : JSON.stringify(x)))
          .join(" • ");
      return JSON.stringify(e);
    }
  } catch {}
  return String(e);
}

// ✅ Unified fetch that matches your BranchCtx stack:
// - cookie auth
// - CSRF header injection
// - x-branch header (BranchCtx)
// - safer parsing
async function fetchJson(path, opts = {}) {
  // apiJSON returns parsed JSON OR throws (your apiBase.js)
  return await apiJSON(path, opts);
}

const RES_STATUSES = [
  "pending",
  "confirmed",
  "seated",
  "completed",
  "cancelled",
  "no-show",
];

function safeDate(v) {
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
function minsDiff(a, b) {
  return Math.round((a.getTime() - b.getTime()) / 60000);
}
function isActiveStatus(statusLower) {
  return (
    statusLower !== "completed" &&
    statusLower !== "cancelled" &&
    statusLower !== "no-show"
  );
}
function formatRelative(mins) {
  if (mins === 0) return "now";
  const abs = Math.abs(mins);
  if (abs < 60) return mins > 0 ? `in ${abs}m` : `${abs}m ago`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const tail = m ? `${h}h ${m}m` : `${h}h`;
  return mins > 0 ? `in ${tail}` : `${tail} ago`;
}
function normStr(s) {
  return String(s ?? "").trim().toLowerCase();
}
function numOr(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/* ============== Component ============== */
export default function Reservations() {
  /* List & filter */
  const [dateFilter, setDateFilter] = useState(() => nowLocalParts().date);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Inline edit buffers
  const [editBuf, setEditBuf] = useState({}); // id -> { status?, table_number?, notes? }
  const [editing, setEditing] = useState({}); // id -> boolean

  // Search + hide toggle
  const [resvQuery, setResvQuery] = useState("");
  const [hideSeated, setHideSeated] = useState(true);

  // ✅ Sorting controls
  const [sortKey, setSortKey] = useState("time"); // time | party | table | status | name | late
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  // ✅ live “now” ticker so late highlighting updates without reloading
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  const listPath = useMemo(() => {
    const base = `/reservations`;
    return dateFilter ? `${base}?date=${encodeURIComponent(dateFilter)}` : base;
  }, [dateFilter]);

  async function load({ silent = false } = {}) {
    if (!silent) setLoading(true);
    setErr("");
    try {
      const data = await fetchJson(listPath);
      const rows = Array.isArray(data)
        ? data
        : data.rows || data.data || data.list || [];
      const arr = Array.isArray(rows) ? rows : [];
      setItems(arr);
      setEditBuf({});
      setEditing({});
    } catch (e) {
      setErr(prettyError(e));
      setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPath]);

  // ✅ Auto-refresh
  useEffect(() => {
    const id = setInterval(() => {
      load({ silent: true }).catch(() => {});
    }, AUTO_REFRESH_SECONDS * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPath]);

  /* New reservation modal */
  const { date: d0, time: t0 } = nowLocalParts();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    party_size: 2,
    notes: "",
    date: d0,
    time: t0,
  });

  function openNewModal() {
    const { date, time } = nowLocalParts();
    setForm({
      customer_name: "",
      phone: "",
      party_size: 2,
      notes: "",
      date,
      time,
    });
    setOpen(true);
  }

  async function createReservation(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const payload = {
      customer_name: form.customer_name.trim(),
      phone: form.phone.trim(),
      party_size: Number(form.party_size) || 1,
      date: form.date,
      time: form.time,
      notes: form.notes || "",
    };
    try {
      await fetchJson(`/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setOpen(false);
      await load();
    } catch (e) {
      setErr(prettyError(e));
    } finally {
      setSaving(false);
    }
  }

  /* Inline edit helpers */
  function startEdit(row) {
    setEditing((prev) => ({ ...prev, [row.id]: true }));
    setEditBuf((prev) => ({
      ...prev,
      [row.id]: {
        status: row.status,
        table_number: row.table_number ?? "",
        notes: row.notes ?? "",
      },
    }));
  }
  function cancelEdit(id) {
    setEditing((prev) => ({ ...prev, [id]: false }));
    setEditBuf((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }
  function onEditChange(id, field, val) {
    setEditBuf((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: val },
    }));
  }

  function hasChanges(row) {
    const buf = editBuf[row.id] || {};
    if (
      Object.prototype.hasOwnProperty.call(buf, "status") &&
      buf.status !== row.status
    )
      return true;
    if (
      Object.prototype.hasOwnProperty.call(buf, "table_number") &&
      (buf.table_number ?? null) !== (row.table_number ?? null)
    )
      return true;
    if (
      Object.prototype.hasOwnProperty.call(buf, "notes") &&
      (buf.notes ?? "") !== (row.notes ?? "")
    )
      return true;
    return false;
  }

  async function saveRow(row) {
    const id = row.id;
    const buf = editBuf[id] || {};
    const calls = [];

    const willBecomeSeated =
      Object.prototype.hasOwnProperty.call(buf, "status") &&
      buf.status !== row.status &&
      String(buf.status).toLowerCase() === "seated";

    if (
      Object.prototype.hasOwnProperty.call(buf, "status") &&
      buf.status !== row.status
    ) {
      calls.push(
        fetchJson(`/reservations/${id}/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: buf.status }),
        })
      );
    }
    if (
      Object.prototype.hasOwnProperty.call(buf, "table_number") &&
      (buf.table_number ?? null) !== (row.table_number ?? null)
    ) {
      calls.push(
        fetchJson(`/reservations/${id}/assign`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ table_number: buf.table_number || null }),
        })
      );
    }
    if (
      Object.prototype.hasOwnProperty.call(buf, "notes") &&
      (buf.notes ?? "") !== (row.notes ?? "")
    ) {
      calls.push(
        fetchJson(`/reservations/${id}/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: buf.notes || "" }),
        })
      );
    }

    if (!calls.length) {
      setEditing((prev) => ({ ...prev, [id]: false }));
      return;
    }

    try {
      if (willBecomeSeated && hideSeated) {
        setItems((prev) => prev.filter((r) => r.id !== id));
        setEditing((prev) => ({ ...prev, [id]: false }));
      }
      await Promise.all(calls);
      await load();
    } catch (e) {
      alert("Save failed: " + prettyError(e));
    }
  }

  async function removeRow(id) {
    if (!window.confirm("Delete reservation?")) return;
    try {
      await fetchJson(`/reservations/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert("Delete failed: " + prettyError(e));
    }
  }

  /* 🔁 Quick toggle: pending ↔ seated directly on emoji click */
  async function quickToggleStatus(row) {
    const current = String(row.status || "").toLowerCase();
    if (current !== "pending" && current !== "seated") return;

    const newStatus = current === "pending" ? "seated" : "pending";
    try {
      await fetchJson(`/reservations/${row.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      await load();
    } catch (e) {
      alert("Quick status update failed: " + prettyError(e));
    }
  }

  /* ---------- Late classification ---------- */
  const nowDate = useMemo(() => new Date(nowTick), [nowTick]);

  function getTimingMeta(r) {
    const start = safeDate(r.start_ts);
    if (!start) return { start: null, minsToStart: null, lateLevel: "none" };

    const statusLower = String(r.status || "").toLowerCase();
    const active = isActiveStatus(statusLower);
    const minsToStart = minsDiff(start, nowDate);

    let lateLevel = "none";
    if (active) {
      const minsPast = -minsToStart;
      if (minsPast >= VERY_LATE_AFTER_MINUTES) lateLevel = "verylate";
      else if (minsPast >= LATE_AFTER_MINUTES) lateLevel = "late";
    }
    return { start, minsToStart, lateLevel };
  }

  /* ---------- Client-side filtering ---------- */
  const filteredItems = useMemo(() => {
    const base = hideSeated
      ? items.filter((r) => String(r.status || "").toLowerCase() !== "seated")
      : items.slice();

    const q = resvQuery.trim().toLowerCase();
    const qd = digits(resvQuery);
    if (!q) return base;

    return base.filter((r) => {
      const name = String(r.customer_name || "").toLowerCase();
      const phone = String(r.phone || "");
      const phoneD = digits(phone);
      const tableStr = String(r.table_number ?? "");
      const status = String(r.status || "").toLowerCase();
      const notes = String(r.notes || "").toLowerCase();
      const source = String(r.source || "").toLowerCase();
      return (
        (q && name.includes(q)) ||
        (qd && phoneD.includes(qd)) ||
        (q && tableStr.includes(q)) ||
        (q && status.includes(q)) ||
        (q && notes.includes(q)) ||
        (q && source.includes(q)) ||
        (q && source === "reservation_app" && "online booking".includes(q))
      );
    });
  }, [items, resvQuery, hideSeated]);

  /* ✅ Sorting (party, time, table, etc.) */
  const sortedItems = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = filteredItems.slice();

    function lateScore(level) {
      if (level === "verylate") return 2;
      if (level === "late") return 1;
      return 0;
    }

    arr.sort((a, b) => {
      const ta = getTimingMeta(a);
      const tb = getTimingMeta(b);

      if (sortKey === "time") {
        const aT = ta.start?.getTime() ?? 0;
        const bT = tb.start?.getTime() ?? 0;
        return (aT - bT) * dir;
      }
      if (sortKey === "party") {
        const aP = numOr(a.party_size, 0);
        const bP = numOr(b.party_size, 0);
        if (aP !== bP) return (aP - bP) * dir;
        const aT = ta.start?.getTime() ?? 0;
        const bT = tb.start?.getTime() ?? 0;
        return (aT - bT) * 1;
      }
      if (sortKey === "table") {
        const aN = numOr(a.table_number, 999999);
        const bN = numOr(b.table_number, 999999);
        if (aN !== bN) return (aN - bN) * dir;
        return (numOr(a.id, 0) - numOr(b.id, 0)) * 1;
      }
      if (sortKey === "status") {
        const aS = normStr(a.status);
        const bS = normStr(b.status);
        return aS.localeCompare(bS) * dir;
      }
      if (sortKey === "name") {
        const aN = normStr(a.customer_name);
        const bN = normStr(b.customer_name);
        return aN.localeCompare(bN) * dir;
      }
      if (sortKey === "late") {
        const aL = lateScore(ta.lateLevel);
        const bL = lateScore(tb.lateLevel);
        if (aL !== bL) return (aL - bL) * dir;
        const aT = ta.start?.getTime() ?? 0;
        const bT = tb.start?.getTime() ?? 0;
        return (aT - bT) * 1;
      }
      return 0;
    });

    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, sortKey, sortDir, nowTick]);

  /* ✅ Small report: total people + total tables used */
  const report = useMemo(() => {
    const people = sortedItems.reduce(
      (sum, r) => sum + Math.max(0, numOr(r.party_size, 0)),
      0
    );
    const tablesSet = new Set(
      sortedItems
        .map((r) => String(r.table_number ?? "").trim())
        .filter((x) => x && x !== "null" && x !== "undefined")
    );
    const tables = tablesSet.size;

    const assigned = sortedItems.filter((r) => {
      const t = String(r.table_number ?? "").trim();
      return !!t && t !== "null" && t !== "undefined";
    }).length;

    return { people, tables, assigned, total: sortedItems.length };
  }, [sortedItems]);

  // ✅ Optional: quick summary counts
  const stats = useMemo(() => {
    let late = 0;
    let verylate = 0;
    let pending = 0;
    let confirmed = 0;
    let seated = 0;
    for (const r of sortedItems) {
      const s = String(r.status || "").toLowerCase();
      if (s === "pending") pending++;
      if (s === "confirmed") confirmed++;
      if (s === "seated") seated++;
      const { lateLevel } = getTimingMeta(r);
      if (lateLevel === "late") late++;
      if (lateLevel === "verylate") verylate++;
    }
    return { pending, confirmed, seated, late, verylate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedItems, nowTick]);

  const visibleCount = sortedItems.length;
  const totalCount = items.length;

  /* ---------- Render ---------- */
  return (
    <div className="resv">
      {/* Top panel */}
      <div className="panel">
        <div className="row" style={{ alignItems: "end" }}>
          <button className="btn ok" onClick={openNewModal}>
            + New Reservation
          </button>

          <div>
            <label
              style={{
                fontSize: 12,
                opacity: 0.85,
                display: "block",
                marginBottom: 4,
              }}
            >
              Date
            </label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setDateFilter(v);
              }}
            />
          </div>

          {/* ✅ Sort controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Sort by
              </label>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
              >
                <option value="time">Time</option>
                <option value="party">Party size</option>
                <option value="table">Table #</option>
                <option value="late">Late level</option>
                <option value="status">Status</option>
                <option value="name">Customer name</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Direction
              </label>
              <select
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value)}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          </div>

          {/* Hide seated toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              id="hideSeated"
              type="checkbox"
              checked={hideSeated}
              onChange={(e) => setHideSeated(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <label htmlFor="hideSeated" style={{ fontSize: 13 }}>
              Hide seated
            </label>
          </div>

          {/* Reservation search */}
          <div style={{ minWidth: 220, flex: 1 }}>
            <label
              style={{
                fontSize: 12,
                opacity: 0.85,
                display: "block",
                marginBottom: 4,
              }}
            >
              Search reservations (name, phone, table, status, notes)
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                placeholder="e.g., Name?, Phone?, table?, confirmed"
                value={resvQuery}
                onChange={(e) => setResvQuery(e.target.value)}
              />
              {resvQuery && (
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => setResvQuery("")}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="resv-stats">
              <span className="chip" data-c="pending">
                pending: {stats.pending}
              </span>
              <span className="chip" data-c="confirmed">
                confirmed: {stats.confirmed}
              </span>
              <span className="chip" data-c="late">
                late: {stats.late}
              </span>
              <span className="chip" data-c="verylate">
                very late: {stats.verylate}
              </span>
              <span style={{ fontSize: 12, opacity: 0.8, marginLeft: 6 }}>
                Showing {visibleCount} of {totalCount}
                {hideSeated ? " (excluding seated)" : ""}
              </span>
            </div>

            {/* ✅ Small report */}
            <div
              className="resv-report"
              style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}
            >
              <span style={{ marginRight: 10 }}>
                Total people: <b>{report.people}</b>
              </span>
              <span style={{ marginRight: 10 }}>
                Total tables used: <b>{report.tables}</b>
              </span>
              <span style={{ marginRight: 10 }}>
                Assigned reservations: <b>{report.assigned}</b> / {report.total}
              </span>
            </div>
          </div>

          <div>
            <button className="btn" onClick={() => load()} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {err && (
          <div
            className="card"
            style={{
              marginTop: 8,
              borderColor: "rgba(239,68,68,.45)",
              background: "rgba(239,68,68,.10)",
            }}
          >
            ! {err}
          </div>
        )}
      </div>

      {/* Table list */}
      <div className="list">
        {sortedItems.length === 0 && !loading && (
          <div className="card">
            No reservations match your search
            {hideSeated ? " (or all are seated)" : ""}.
          </div>
        )}

        {sortedItems.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Party</th>
                  <th>Table #</th>
                  <th>Start</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((r) => {
                  const isEdit = !!editing[r.id];
                  const buf = editBuf[r.id] || {};
                  const statusVal = isEdit ? buf.status ?? r.status : r.status;
                  const tableVal = isEdit
                    ? buf.table_number ?? (r.table_number || "")
                    : r.table_number || "";
                  const notesVal = isEdit
                    ? buf.notes ?? (r.notes || "")
                    : r.notes || "";

                  const statusLower = String(statusVal || "").toLowerCase();
                  const { start, minsToStart, lateLevel } = getTimingMeta({
                    ...r,
                    status: statusVal,
                  });

                  const rowClass =
                    lateLevel === "verylate"
                      ? "resv-row verylate"
                      : lateLevel === "late"
                      ? "resv-row late"
                      : "resv-row";

                  return (
                    <tr key={r.id} className={rowClass}>
                      <td>#{r.id}</td>

                      <td style={{ fontWeight: 600 }}>
                        <div>{r.customer_name}</div>
                        {String(r.source || "").toLowerCase() === "reservation_app" && (
                          <div style={{ marginTop: 6 }}>
                            <span
                              className="chip"
                              data-c="confirmed"
                              title="Created by the public reservation application"
                            >
                              🌐 Online booking
                            </span>
                          </div>
                        )}
                        {lateLevel !== "none" && (
                          <span className="late-badge" data-l={lateLevel}>
                            {lateLevel === "verylate" ? "LATE++" : "LATE"}
                          </span>
                        )}
                      </td>

                      <td>{r.phone}</td>
                      <td>{r.party_size}</td>

                      <td>
                        {isEdit ? (
                          <select
                            value={tableVal || ""}
                            onChange={(e) =>
                              onEditChange(
                                r.id,
                                "table_number",
                                e.target.value || null
                              )
                            }
                          >
                            <option value="">(unassigned)</option>
                            {Array.from({ length: 110 }, (_, i) =>
                              String(i + 1)
                            ).map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        ) : (
                          tableVal || ""
                        )}
                      </td>

                      <td style={{ fontSize: 12, opacity: 0.95 }}>
                        <div>{start ? start.toLocaleString() : r.start_ts}</div>
                        {typeof minsToStart === "number" && (
                          <div
                            className="time-rel"
                            data-l={lateLevel}
                            title="Relative to now"
                          >
                            {formatRelative(minsToStart)}
                          </div>
                        )}
                      </td>

                      <td>
                        {isEdit ? (
                          <select
                            value={statusVal}
                            onChange={(e) =>
                              onEditChange(r.id, "status", e.target.value)
                            }
                          >
                            {RES_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        ) : statusLower === "pending" ? (
                          <span
                            className="status"
                            data-s="pending"
                            title="Click to mark as seated"
                            onClick={() => quickToggleStatus(r)}
                            style={{ cursor: "pointer" }}
                          >
                            ⏳
                          </span>
                        ) : statusLower === "seated" ? (
                          <span
                            className="status seated"
                            data-s="seated"
                            title="Click to mark as pending"
                            onClick={() => quickToggleStatus(r)}
                            style={{ cursor: "pointer" }}
                          >
                            🪑
                          </span>
                        ) : (
                          <span className="status" data-s={statusLower}>
                            {statusVal}
                          </span>
                        )}
                      </td>

                      <td>
                        {isEdit ? (
                          <textarea
                            value={notesVal}
                            onChange={(e) =>
                              onEditChange(r.id, "notes", e.target.value)
                            }
                            placeholder="Notes..."
                            rows={2}
                            style={{ resize: "vertical", minWidth: 160 }}
                          />
                        ) : (
                          <div style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>
                            {notesVal}
                          </div>
                        )}
                      </td>

                      <td style={{ whiteSpace: "nowrap" }}>
                        {!isEdit ? (
                          <>
                            <button
                              className="btn ok"
                              onClick={() => startEdit(r)}
                              style={{ marginRight: 6 }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn danger"
                              onClick={() => removeRow(r.id)}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn ok"
                              onClick={() => saveRow(r)}
                              style={{ marginRight: 6 }}
                              disabled={!hasChanges(r)}
                            >
                              Save
                            </button>
                            <button
                              className="btn ghost"
                              onClick={() => cancelEdit(r.id)}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="resv-footnote">
              Late rules: turns red after {LATE_AFTER_MINUTES}m past start;
              darker after {VERY_LATE_AFTER_MINUTES}m. Auto-refresh every{" "}
              {AUTO_REFRESH_SECONDS}s.
            </div>
          </div>
        )}
      </div>

      {/* Modal: New Reservation */}
      {open && (
        <div className="overlay" onClick={() => setOpen(false)}>
          <div
            className="panel modal-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="row"
              style={{
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>New Reservation</h3>
              <button className="btn ghost" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>

            <form onSubmit={createReservation}>
              {/* Contact search */}
              <ContactSearch
                onPick={(c) => {
                  setForm((f) => ({
                    ...f,
                    customer_name: c.name || f.customer_name || "(no name)",
                    phone: c.phone || f.phone,
                  }));
                }}
              />

              <div className="row">
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Customer
                  </label>
                  <input
                    required
                    placeholder="Name"
                    value={form.customer_name}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        customer_name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Phone
                  </label>
                  <input
                    required
                    placeholder="9613xxxxxx"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        phone: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="row">
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Party Size
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={form.party_size}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        party_size: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={form.date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Time
                  </label>
                  <input
                    type="time"
                    required
                    value={form.time}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, time: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="row">
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Notes
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, notes: e.target.value }))
                    }
                    placeholder="Allergies, occasion, etc."
                  />
                </div>
              </div>

              <div className="tools" style={{ marginTop: 8 }}>
                <button type="submit" className="btn ok" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Small subcomponent for contact search --- */
function ContactSearch({ onPick }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [results, setResults] = useState([]);
  const [kbdIndex, setKbdIndex] = useState(-1);
  const wrapRef = useRef(null);

  function digitsLocal(s) {
    return String(s || "").replace(/\D+/g, "");
  }

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick, { passive: true });
    document.addEventListener("touchstart", onDocClick, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, []);

  useEffect(() => {
    if (!search || search.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    setErr("");
    const id = setTimeout(async () => {
      try {
        // ✅ BranchCtx-safe call
        const data = await apiJSON(
          `/contacts?search=${encodeURIComponent(search.trim())}`
        );

        let arr = [];
        if (Array.isArray(data)) arr = data;
        else if (data && typeof data === "object") {
          if (Array.isArray(data.contacts)) arr = data.contacts;
          else if (Array.isArray(data.rows)) arr = data.rows;
          else if (Array.isArray(data.data)) arr = data.data;
          else if (Array.isArray(data.list)) arr = data.list;
          else if ("name" in data || "phones" in data) arr = [data];
        }

        const qLower = search.trim().toLowerCase();
        const qDigits = digitsLocal(search);

        const filtered = arr.filter((r) => {
          const nm = String(r.name || "").toLowerCase();
          const ph = String(r.phones || "");
          const phDigits = digitsLocal(ph);
          return (
            (nm && nm.includes(qLower)) ||
            (qDigits && phDigits.includes(qDigits))
          );
        });

        const mapped = filtered.map((r) => {
          const phone = String(r.phones || "").split(/[;,]/)[0].trim();
          const l8 = digitsLocal(phone).slice(-8);
          return { name: r.name || "", phone, last8: l8 };
        });

        mapped.sort((a, b) => {
          const aExact = qDigits && a.last8 === qDigits ? 0 : 1;
          const bExact = qDigits && b.last8 === qDigits ? 0 : 1;
          if (aExact !== bExact) return aExact - bExact;
          const aHas = qDigits && digitsLocal(a.phone).includes(qDigits) ? 0 : 1;
          const bHas = qDigits && digitsLocal(b.phone).includes(qDigits) ? 0 : 1;
          if (aHas !== bHas) return aHas - bHas;
          return (a.name || "").localeCompare(b.name || "");
        });

        setResults(mapped.slice(0, 20));
      } catch (e) {
        setErr(prettyError(e));
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setKbdIndex(-1);
  }, [results, open]);

  function pick(c) {
    onPick && onPick(c);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", marginBottom: 10 }}>
      <label
        style={{
          fontSize: 12,
          opacity: 0.85,
          display: "block",
          marginBottom: 4,
        }}
      >
        Search contact (name or phone)
      </label>
      <input
        placeholder="Type at least 2 characters..."
        value={search}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setKbdIndex((i) => Math.min(i + 1, results.length - 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setKbdIndex((i) => Math.max(i - 1, 0));
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (kbdIndex >= 0) pick(results[kbdIndex]);
          }
          if (e.key === "Escape") setOpen(false);
        }}
        autoFocus
      />
      {open && (
        <div className="dropdown">
          {loading && <div className="drop-item">Searching...</div>}
          {err && (
            <div className="drop-item" style={{ color: "#fecaca" }}>
              ! {err}
            </div>
          )}
          {!loading &&
            !err &&
            search.trim().length >= 2 &&
            results.length === 0 && <div className="drop-item">(No results)</div>}
          {results.map((c, i) => (
            <div
              key={i}
              className={`drop-item ${i === kbdIndex ? "focus" : ""}`}
              onMouseEnter={() => setKbdIndex(i)}
              onClick={() => pick(c)}
            >
              <div style={{ fontWeight: 600 }}>{c.name || "(no name)"}</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                {c.phone || "no phone"} • last 8:{" "}
                <span style={{ opacity: 1 }}>{c.last8 || "-"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}